<template>
  <div class="grid gap-4">
    <OrganizationCard
      v-if="relatedOrganization"
      :org="relatedOrganization"
      class="hover:bg-neutral-100 dark:hover:bg-neutral-800 text-sm transition-colors !max-h-full duration-75 min-w-0"
      @click.stop
    />
    <div class="flex gap-2">
      <TitleFollowButton
        :title-id="title.id"
        pill
        single
        :follow-text="{
          follow: sm
            ? translate('components.reader.endCard.button.followTitleLong')
            : translate('components.reader.endCard.button.followTitle'),
        }"
      />
      <NamiButton
        :button-type="isLiked ? 'primary' : 'secondary'"
        :icon="IconHeart"
        pill
        :force-icon-only="!sm && !likeFetched"
        :disabled="!likeFetched"
        @click.prevent.stop="toggleLike"
        >{{ likeCount || "Like" }}
      </NamiButton>
      <ContextMenuList
        closeOnOutsideClick
        defaultPosition="bottom-right"
        :options="[
          {
            name: 'Facebook',
            href: `https://www.facebook.com/sharer/sharer.php?u=https://namicomi.com/${locale}/chapter/${chapter.id}`,
            icon: IconBrandFacebook,
          },
          {
            name: 'Reddit',
            href: redditShareUrl,
            icon: IconBrandReddit,
          },
          {
            name: 'Twitter',
            href: twitterShareUrl,
            icon: IconBrandTwitter,
          },
          {
            name: 'Copy URL',
            action: () => {
              copyTextToClipboard(`https://nami.moe/c/${chapter.id}`);
            },
            icon: IconCopy,
          },
        ]"
      >
        <template v-slot="{ isOpen, open, close }">
          <NamiButton
            button-type="secondary"
            :icon="IconShare"
            pill
            no-waves
            :force-icon-only="!sm"
            @click.prevent.stop="() => (isOpen ? close() : open())"
            >Share
          </NamiButton>
        </template>
      </ContextMenuList>
      <!--            <NamiButton-->
      <!--              button-type="secondary"-->
      <!--              :icon="IconSend"-->
      <!--              pill-->
      <!--              :force-icon-only="!sm"-->
      <!--              @click.prevent.stop-->
      <!--              >Post on Social-->
      <!--            </NamiButton>-->
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  IconBookmark,
  IconBrandFacebook,
  IconBrandReddit,
  IconBrandTwitter,
  IconCopy,
  IconHeart,
  IconShare,
} from "@tabler/icons-vue";
import {
  type ChapterEntity,
  getRelationship,
  type TitleEntity,
} from "~/src/api";
import { useAppStore } from "~/stores/app";
import { parseTitleForDisplay } from "~/utils/display/title";
import { parseChapterForDisplay } from "~/utils/display/chapter";
import TitleFollowButton from "~/components/title/TitleFollowButton.vue";
import { useQueryClient } from "@tanstack/vue-query";

interface Props {
  title: TitleEntity;
  chapter: ChapterEntity;
}

const props = defineProps<Props>();
const nuxtApp = useNuxtApp();
const { locale, t: translate } = useI18n();
const { sm } = useBreakpoints();
const client = useQueryClient();

const {
  targetState: isLiked,
  ready: likeFetched,
  updateState,
  likeCount,
} = useGlobalChapterLike(() => client, props.chapter.id);

const toggleLike = () => updateState(!isLiked.value);

const relatedOrganization = computed(() => {
  // Throws error if organization is not expanded
  return getRelationship(props.title, "organization", true);
});

// share URLs
const getSanitizedTagName = (id: string) => {
  const translated = translate(`components.tag.${id}.name`);
  return translated.replace(/\s/g, "").replace(/'/g, "");
};

const twitterShareUrl = computed(() => {
  const url = new URL("https://twitter.com/intent/tweet");

  const parsedTitle = parseTitleForDisplay(props.title);
  const parsedChapter = parseChapterForDisplay(props.chapter);

  let text = `Reading ${
    parsedTitle.title
  }, ${parsedChapter.shortName.trim()} on NamiComi!\n\nhttps://namicomi.com/${
    locale.value
  }/chapter/${props.chapter.id}\n\n`;

  if (parsedTitle.primaryGenre) {
    const tagName1 = getSanitizedTagName(parsedTitle.primaryGenre.id);

    text += `#${tagName1}`;

    if (parsedTitle.secondaryGenre) {
      const tagName2 = getSanitizedTagName(parsedTitle.secondaryGenre.id);
      text += ` #${tagName2}`;
    }

    text += " #NamiComi";
  } else {
    text += "#NamiComi";
  }

  url.searchParams.set("text", text);

  return url.href;
});

const redditShareUrl = computed(() => {
  const url = new URL("https://www.reddit.com/submit");
  const parsedTitle = parseTitleForDisplay(props.title);
  const parsedChapter = parseChapterForDisplay(props.chapter);

  url.searchParams.set(
    "url",
    `https://namicomi.com/${locale.value}/chapter/${props.chapter.id}`,
  );

  // let title = `I am reading $title on NamiComi: $MainGenre and $SecondGenre with $Theme1 and $Theme2  themes!`
  let postTitle = `I am reading ${
    parsedTitle.title
  }, ${parsedChapter.shortName.trim()} on NamiComi:`;

  const themes = parsedTitle.tags
    .filter((t) => t.attributes?.group === "theme")
    .map((t) => t.id);

  if (parsedTitle.primaryGenre) {
    postTitle += ` ${getSanitizedTagName(parsedTitle.primaryGenre.id)}`;

    if (parsedTitle.secondaryGenre) {
      postTitle += ` and ${getSanitizedTagName(parsedTitle.secondaryGenre.id)}`;
    }

    if (themes[0]) {
      postTitle += " with";
    }
  }

  if (themes[0]) {
    postTitle += ` ${getSanitizedTagName(themes[0])}`;

    if (themes[1]) {
      postTitle += ` and ${getSanitizedTagName(themes[1])}`;
    }

    postTitle += " themes!";
  }

  url.searchParams.set("title", postTitle);

  return url.href;
});
</script>

<style scoped lang="postcss"></style>
